import { MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import { MilestoneMap1 } from '../types';
import { ZOOM_LEVELS } from '@mm-shared/intervals';

// Re-exported so existing mm-web consumers keep a single zoom entry point.
export { ZOOM_LEVELS, isCoarserInterval, isFinerInterval } from '@mm-shared/intervals';

// Maximum number of columns allowed for safety
// 1000 allows 10-15 year maps to zoom to weeks while blocking problematic hours/days levels
export const MAX_ZOOM_COLUMNS = 1000;

/**
 * Returns the next finer zoom level (zoom in)
 * hours <- days <- weeks <- months <- quarters <- years
 */
export function getZoomInFromLevel(
  currentLevel: MilestoneMapInterval,
): MilestoneMapInterval | null {
  const currentIndex = ZOOM_LEVELS.indexOf(currentLevel);
  if (currentIndex <= 0) return null; // Already at finest level
  return ZOOM_LEVELS[currentIndex - 1];
}

/**
 * Returns the next coarser zoom level (zoom out)
 * hours -> days -> weeks -> months -> quarters -> years
 */
export function getZoomOutFromLevel(
  currentLevel: MilestoneMapInterval,
): MilestoneMapInterval | null {
  const currentIndex = ZOOM_LEVELS.indexOf(currentLevel);
  if (currentIndex >= ZOOM_LEVELS.length - 1) return null; // Already at coarsest level
  return ZOOM_LEVELS[currentIndex + 1];
}

/**
 * Estimates the number of intervals that would be generated for a given zoom level.
 * This is a fast approximation to avoid generating all intervals.
 * Used for safety checks to prevent excessive column counts.
 */
export function estimateIntervalCount(
  startDate: Date,
  endDate: Date,
  intervalType: MilestoneMapInterval,
): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();

  switch (intervalType) {
    case 'hours':
      return Math.ceil(diffMs / (1000 * 60 * 60)); // milliseconds to hours
    case 'days':
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24)); // milliseconds to days
    case 'weeks':
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24 * 7)); // milliseconds to weeks
    case 'months': {
      // Approximate: 30.44 days per month on average
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24 * 30.44));
    }
    case 'quarters':
      // 4 quarters per year
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24 * 365.25 / 4));
    case 'years':
      // 365.25 days per year (accounting for leap years)
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24 * 365.25));
    default:
      return 0;
  }
}

/**
 * Checks if zooming to a specific level would exceed the maximum column count.
 * Returns true if safe to zoom, false if it would create too many columns.
 */
export function canZoomToLevel(
  map: MilestoneMap1,
  targetLevel: MilestoneMapInterval,
): boolean {
  const startDate = new Date(map.startDate);
  const endDate = new Date(map.endDate);
  const estimatedCount = estimateIntervalCount(startDate, endDate, targetLevel);
  return estimatedCount <= MAX_ZOOM_COLUMNS;
}
